import { useEffect, useMemo, useState } from 'react';
import { getDesktopDownloadUrl } from '@shared';
import {
  Activity,
  CirclePause,
  CirclePlay,
  Database,
  Download,
  MonitorCheck,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { FadeIn } from '@/components/animations/FadeIn';
import { Button } from '@/components/ui/Button';
import { DeleteConfirmation } from '@/components/ui/DeleteConfirmation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/Checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { DaySheetlyInput } from '@/components/fields/DaySheetlyInput';
import { DaySheetlySelect } from '@/components/fields/DaySheetlySelect';
import { getActivityBridge } from '../activityAssistant.service';
import type {
  ActivityBridge,
  ActivityRule,
  ActivitySettings,
  ActivityStatus,
} from '../activityAssistant.types';

const retentionOptions = [
  { value: '1', label: '1 day' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
] as const;

const initialSettings: ActivitySettings = {
  enabled: false,
  paused: false,
  startAtLogin: false,
  retentionDays: 7,
  idleThresholdMinutes: 5,
  excludedApps: ['daysheetly'],
};

function statusLabel(status: ActivityStatus | null): string {
  if (!status) return 'Checking';
  return status.state.charAt(0).toUpperCase() + status.state.slice(1);
}

export function ActivityAssistantSettingsPage({
  bridge = getActivityBridge(),
}: {
  bridge?: ActivityBridge;
}) {
  const [status, setStatus] = useState<ActivityStatus | null>(null);
  const [settings, setSettings] = useState<ActivitySettings>(initialSettings);
  const [loading, setLoading] = useState(true);
  const [consentOpen, setConsentOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [excludedApp, setExcludedApp] = useState('');
  const [rules, setRules] = useState<ActivityRule[]>([]);
  const [editingRule, setEditingRule] = useState<ActivityRule | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([bridge.getStatus(), bridge.getSettings(), bridge.listRules()])
      .then(([nextStatus, nextSettings, nextRules]) => {
        if (cancelled) return;
        setStatus(nextStatus);
        setSettings(nextSettings);
        setRules(nextRules);
      })
      .catch((error: Error) => {
        if (!cancelled) toast.error(error.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bridge]);

  const isWeb = status?.environment === 'web';
  const isSupported = Boolean(status?.supported);
  const stateTone = useMemo(() => {
    if (status?.state === 'active') return 'bg-emerald-500';
    if (status?.state === 'error') return 'bg-destructive';
    if (status?.state === 'unsupported') return 'bg-amber-500';
    return 'bg-muted-foreground';
  }, [status?.state]);

  const persistSettings = async (changes: Partial<ActivitySettings>) => {
    const previous = settings;
    setSettings((current) => ({ ...current, ...changes }));
    try {
      const saved = await bridge.updateSettings(changes);
      setSettings(saved);
    } catch (error) {
      setSettings(previous);
      toast.error(error instanceof Error ? error.message : 'Could not update settings');
    }
  };

  const enableAssistant = async () => {
    try {
      const nextStatus = await bridge.setEnabled(true);
      setStatus(nextStatus);
      setSettings((current) => ({ ...current, enabled: true, paused: false }));
      setConsentOpen(false);
      toast.success('Activity assistant enabled on this device');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not enable activity assistant');
    }
  };

  const disableAssistant = async (deleteLocalData = false) => {
    try {
      const nextStatus = await bridge.setEnabled(false);
      if (deleteLocalData) await bridge.deleteAllActivityData();
      setStatus(nextStatus);
      setSettings((current) => ({ ...current, enabled: false, paused: false }));
      setDisableOpen(false);
      toast.success('Activity assistant disabled');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not disable activity assistant');
    }
  };

  const togglePaused = async () => {
    try {
      const next = !settings.paused;
      const nextStatus = await bridge.setPaused(next);
      setStatus(nextStatus);
      setSettings((current) => ({ ...current, paused: next }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not change collection state');
    }
  };

  const addExcludedApp = () => {
    const normalized = excludedApp.trim().toLocaleLowerCase();
    if (!normalized || settings.excludedApps.includes(normalized)) return;
    void persistSettings({ excludedApps: [...settings.excludedApps, normalized] });
    setExcludedApp('');
  };

  const exportDiagnostics = async () => {
    try {
      const diagnostics = await bridge.exportDiagnostics();
      const url = URL.createObjectURL(
        new Blob([diagnostics], { type: 'application/json;charset=utf-8' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `daysheetly-activity-diagnostics-${new Date()
        .toISOString()
        .slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not export diagnostics');
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 py-6">
      <FadeIn>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Activity assistant</h1>
          <p className="text-sm text-muted-foreground">
            Reconstruct work you forgot to log, while keeping raw activity on this device.
          </p>
        </div>
      </FadeIn>

      <FadeIn delay={0.05}>
        <Card>
          <CardHeader className="gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1.5">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Activity className="h-5 w-5" />
                Help me register activities automatically
              </CardTitle>
              <CardDescription>
                DaySheetly observes the active application and window title. It does not capture
                your screen, keystrokes, clipboard, microphone or camera.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium">
              <span className={`h-2 w-2 rounded-full ${stateTone}`} />
              {loading ? 'Checking' : statusLabel(status)}
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {isWeb ? (
              <div className="flex flex-col gap-4 rounded-lg border border-dashed bg-muted/30 p-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex gap-3">
                  <MonitorCheck className="mt-0.5 h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium">Available in the desktop app</p>
                    <p className="text-sm text-muted-foreground">
                      Browsers cannot reliably inspect other applications. Install DaySheetly for
                      Windows or Linux to use this feature.
                    </p>
                  </div>
                </div>
                <Button asChild variant="outline">
                  <a href={getDesktopDownloadUrl(process.env.DAYSHEETLY_WEBSITE_URL)}>
                    <Download className="mr-2 h-4 w-4" /> Download desktop app
                  </a>
                </Button>
              </div>
            ) : !isSupported ? (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
                {status?.message || 'Activity detection is not supported in this desktop session.'}
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="activity-assistant-enabled"
                    checked={settings.enabled}
                    onCheckedChange={(checked) => {
                      if (checked) setConsentOpen(true);
                      else setDisableOpen(true);
                    }}
                    aria-label="Help me register activities automatically"
                  />
                  <label htmlFor="activity-assistant-enabled" className="text-sm font-medium">
                    Enabled on this device
                  </label>
                </div>
                {settings.enabled ? (
                  <Button type="button" size="sm" variant="outline" onClick={togglePaused}>
                    {settings.paused ? (
                      <CirclePlay className="mr-2 h-4 w-4" />
                    ) : (
                      <CirclePause className="mr-2 h-4 w-4" />
                    )}
                    {settings.paused ? 'Resume' : 'Pause'}
                  </Button>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>
      </FadeIn>

      {!isWeb && isSupported ? (
        <FadeIn delay={0.1} className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Device settings</CardTitle>
              <CardDescription>These choices stay on this computer.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="activity-start-at-login"
                  checked={settings.startAtLogin}
                  onCheckedChange={(checked) =>
                    void persistSettings({ startAtLogin: Boolean(checked) })
                  }
                  aria-label="Start DaySheetly when I sign in"
                />
                <div>
                  <label htmlFor="activity-start-at-login" className="text-sm font-medium">
                    Start DaySheetly when I sign in
                  </label>
                  <p className="text-xs text-muted-foreground">
                    This is separate from enabling activity collection.
                  </p>
                </div>
              </div>
              <DaySheetlySelect
                label="Keep local activity for"
                value={String(settings.retentionDays) as '1' | '7' | '30'}
                onValueChange={(value) =>
                  value && void persistSettings({ retentionDays: Number(value) as 1 | 7 | 30 })
                }
                options={retentionOptions}
                testId="activity-retention"
              />
              <DaySheetlyInput
                variant="number"
                label="Mark as idle after (minutes)"
                value={settings.idleThresholdMinutes}
                min={1}
                max={30}
                onValueChange={(value) => {
                  if (typeof value !== 'number') return;
                  void persistSettings({ idleThresholdMinutes: Math.min(30, Math.max(1, value)) });
                }}
                testId="activity-idle-threshold"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Excluded applications</CardTitle>
              <CardDescription>
                Excluded apps are never added to your local activity history.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  <DaySheetlyInput
                    label="Process or application name"
                    value={excludedApp}
                    onValueChange={(value) => setExcludedApp(String(value ?? ''))}
                    placeholder="e.g. 1password"
                    testId="activity-excluded-app"
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        addExcludedApp();
                      }
                    }}
                  />
                </div>
                <Button type="button" size="icon" variant="outline" onClick={addExcludedApp} aria-label="Add excluded application">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="activity-excluded-apps">
                {settings.excludedApps.map((app) => (
                  <span key={app} className="inline-flex items-center gap-1 rounded-full border bg-muted px-3 py-1 text-xs">
                    {app}
                    <button
                      type="button"
                      onClick={() =>
                        void persistSettings({
                          excludedApps: settings.excludedApps.filter((item) => item !== app),
                        })
                      }
                      aria-label={`Remove ${app} from excluded applications`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        </FadeIn>
      ) : null}

      {!isWeb ? (
        <FadeIn delay={0.15}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Database className="h-5 w-5" /> Local data and diagnostics
              </CardTitle>
              <CardDescription>
                Raw activity is not uploaded to your workspace or visible to other members.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                <dt className="text-muted-foreground">Environment</dt>
                <dd>{status?.environment || 'unknown'}</dd>
                <dt className="text-muted-foreground">Last local event</dt>
                <dd>{status?.lastEventAt ? new Date(status.lastEventAt).toLocaleString() : 'None'}</dd>
              </dl>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={() => void exportDiagnostics()}>
                  <Download className="mr-2 h-4 w-4" /> Export diagnostics
                </Button>
                <Button type="button" variant="destructive" onClick={() => setDeleteOpen(true)}>
                  <Trash2 className="mr-2 h-4 w-4" /> Delete all local activity data
                </Button>
              </div>
            </CardContent>
          </Card>
        </FadeIn>
      ) : null}

      {!isWeb ? (
        <FadeIn delay={0.2}>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Local association rules</CardTitle>
              <CardDescription>
                Rules suggest a project or title during review. They never create tasks by
                themselves.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {rules.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No saved rules. Use Remember association while reviewing an activity.
                </p>
              ) : (
                rules.map((rule) => (
                  <div
                    key={rule.id}
                    className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0 text-sm">
                      <p className="font-medium">
                        {rule.processName ? `Process is ${rule.processName}` : 'Any process'}
                        {rule.titleContains ? ` · title contains “${rule.titleContains}”` : ''}
                      </p>
                      <p className="truncate text-muted-foreground">
                        Project {rule.projectId || 'unchanged'}
                        {rule.suggestedTitle ? ` · title “${rule.suggestedTitle}”` : ''}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingRule({ ...rule })}
                      >
                        <Pencil className="mr-2 h-3.5 w-3.5" /> Edit
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          try {
                            await bridge.deleteRule(rule.id);
                            setRules((current) => current.filter((item) => item.id !== rule.id));
                          } catch (error) {
                            toast.error(
                              error instanceof Error ? error.message : 'Could not delete rule',
                            );
                          }
                        }}
                      >
                        <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </FadeIn>
      ) : null}

      <Dialog open={consentOpen} onOpenChange={setConsentOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enable activity assistant on this device?</DialogTitle>
            <DialogDescription>
              Window titles may contain file, page or client names. They remain in local storage
              until you delete them or the retention period expires.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 text-sm sm:grid-cols-2">
            <div className="rounded-lg border p-4">
              <p className="mb-2 flex items-center gap-2 font-medium">
                <MonitorCheck className="h-4 w-4 text-primary" /> Collected locally
              </p>
              <ul className="list-disc space-y-1 pl-4 text-muted-foreground">
                <li>Application and process name</li>
                <li>Active window title</li>
                <li>Start and end time</li>
              </ul>
            </div>
            <div className="rounded-lg border p-4">
              <p className="mb-2 flex items-center gap-2 font-medium">
                <ShieldCheck className="h-4 w-4 text-emerald-600" /> Never collected
              </p>
              <ul className="list-disc space-y-1 pl-4 text-muted-foreground">
                <li>Screenshots or screen recording</li>
                <li>Keystrokes or clipboard</li>
                <li>Microphone or camera</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConsentOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={enableAssistant}>
              Enable activity assistant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmation
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete all local activity data?"
        description="This removes local segments and association rules. Tasks already added to DaySheetly are not affected."
        confirmLabel="Delete local data"
        onConfirm={async () => {
          try {
            await bridge.deleteAllActivityData();
            setDeleteOpen(false);
            toast.success('Local activity data deleted');
          } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Could not delete local data');
          }
        }}
        testId="activity-delete-confirmation"
      />

      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable activity assistant?</DialogTitle>
            <DialogDescription>
              Collection stops immediately. You can keep the local history for review or delete
              all local activities and association rules now.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDisableOpen(false)}>
              Cancel
            </Button>
            <Button type="button" variant="outline" onClick={() => void disableAssistant(false)}>
              Disable and keep data
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void disableAssistant(true)}
            >
              Disable and delete data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(editingRule)}
        onOpenChange={(open) => {
          if (!open) setEditingRule(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit local association rule</DialogTitle>
            <DialogDescription>
              Conditions and suggestions stay only on this device.
            </DialogDescription>
          </DialogHeader>
          {editingRule ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <DaySheetlyInput
                label="Process equals"
                value={editingRule.processName || ''}
                onValueChange={(value) =>
                  setEditingRule((current) =>
                    current ? { ...current, processName: String(value || '') || null } : current,
                  )
                }
              />
              <DaySheetlyInput
                label="Window title contains"
                value={editingRule.titleContains || ''}
                onValueChange={(value) =>
                  setEditingRule((current) =>
                    current ? { ...current, titleContains: String(value || '') || null } : current,
                  )
                }
              />
              <DaySheetlyInput
                label="Project ID"
                value={editingRule.projectId || ''}
                onValueChange={(value) =>
                  setEditingRule((current) =>
                    current ? { ...current, projectId: String(value || '') || null } : current,
                  )
                }
              />
              <DaySheetlyInput
                label="Suggested task title"
                value={editingRule.suggestedTitle || ''}
                onValueChange={(value) =>
                  setEditingRule((current) =>
                    current ? { ...current, suggestedTitle: String(value || '') || null } : current,
                  )
                }
              />
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditingRule(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={async () => {
                if (!editingRule) return;
                try {
                  const saved = await bridge.saveRule(editingRule);
                  setRules((current) => [
                    ...current.filter((item) => item.id !== saved.id),
                    saved,
                  ]);
                  setEditingRule(null);
                  toast.success('Association rule updated');
                } catch (error) {
                  toast.error(error instanceof Error ? error.message : 'Could not update rule');
                }
              }}
            >
              Save rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
